import { useState, useEffect, useRef, useCallback } from "react"
import {
  Search,
  X,
  ChevronDown,
  ChevronRight,
  Users,
  Clock,
  Briefcase,
  RefreshCw,
  Check,
  Archive,
} from "lucide-react"
import EmailModal from "./EmailModal"
import EmailModalMany from "./EmailModalMany"
import ArchiveLeadModal from "./ArchiveLeadModal"
import CandidateDetailsContent from "../components/job-details/CandidateDetailsContent"

// ── Design tokens (shared with JobIdInterviews / EmailModal / Leads) ──────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border:       "0.5px solid #e5e7eb",
  borderHover:  "0.5px solid #d1d5db",
  radius:       8,
  radiusCard:   10,
  bg:           "#fff",
  bgPage:       "#f5f5f0",
  bgMuted:      "#fafaf9",
  text:         "#111827",
  textMuted:    "#374151",
  textTertiary: "#6b7280",
  shadow:       "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

// Checkbox col (28px) prepended — matches Leads.jsx exactly
const GRID_COLS = "28px minmax(0,2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 96px"

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length]
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()

const createCandidateContext = ({ candidate, jobListing }) => ({
  candidate: {
    firstName: candidate?.applicantName?.split(" ")[0] ?? null,
    fullName:  candidate?.applicantName ?? null,
  },
  job: {
    role:    jobListing?.role ?? null,
    company: jobListing?.organizationName ?? null,
  },
})

const getScoreConfig = (score, matchLevel) => {
  const verdict = (matchLevel || "").replace(/_/g, " ").toUpperCase().trim()
  if (score == null)  return { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb", dot: "#d1d5db", verdict: "—" }
  if (score >= 75)    return { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", dot: "#10b981", verdict: verdict || "STRONG MATCH"  }
  if (score >= 50)    return { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6", verdict: verdict || "GOOD MATCH"    }
  if (score >= 25)    return { bg: "#fffbeb", color: "#92400e", border: "#fde68a", dot: "#f59e0b", verdict: verdict || "PARTIAL MATCH" }
  return                     { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#ef4444", verdict: verdict || "WEAK MATCH"    }
}

const SORT_COMPARATORS = {
  appliedAsc:  (a, b) => new Date(a.appliedAt || 0) - new Date(b.appliedAt || 0),
  appliedDesc: (a, b) => new Date(b.appliedAt || 0) - new Date(a.appliedAt || 0),
  bestMatch:   (a, b) => (b.matchingScore?.finalScore || 0) - (a.matchingScore?.finalScore || 0),
  expDesc:     (a, b) => (parseInt(b.personal_information?.yearsExperience) || 0) - (parseInt(a.personal_information?.yearsExperience) || 0),
  expAsc:      (a, b) => (parseInt(a.personal_information?.yearsExperience) || 0) - (parseInt(b.personal_information?.yearsExperience) || 0),
}

const SORT_OPTIONS = [
  { value: "appliedDesc", label: "Newest first"  },
  { value: "appliedAsc",  label: "Oldest first"  },
  { value: "bestMatch",   label: "Best match"    },
  { value: "expDesc",     label: "Experience ↓"  },
  { value: "expAsc",      label: "Experience ↑"  },
]

// ── Checkbox (identical to Leads.jsx) ─────────────────────────────────────────
const Checkbox = ({ checked, onClick }) => (
  <div
    onClick={(e) => { e.stopPropagation(); onClick?.(e) }}
    style={{
      width: 16,
      height: 16,
      borderRadius: 4,
      border: `1.5px solid ${checked ? "#111827" : "#d1d5db"}`,
      background: checked ? "#111827" : T.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      cursor: "pointer",
      transition: "border-color 0.12s, background 0.12s",
    }}
  >
    {checked && (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
        <polyline points="2,6 5,9 10,3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </div>
)

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name }) => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: 7,
      background: getAvatarGradient(name),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      fontFamily: FF,
      letterSpacing: "0.02em",
    }}
  >
    {getInitials(name)}
  </div>
)

// ── Score cell ────────────────────────────────────────────────────────────────
const ScoreCell = ({ score, matchLevel }) => {
  if (score == null) {
    return (
      <span style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>
        Not evaluated
      </span>
    )
  }
  const cfg = getScoreConfig(score, matchLevel)
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      <div>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: cfg.color, fontFamily: MONO, lineHeight: 1 }}>
          {cfg.verdict}
        </p>
        <p style={{ margin: "3px 0 0", fontSize: 13, fontWeight: 700, color: T.text, fontFamily: FF, lineHeight: 1 }}>
          {score}%
        </p>
      </div>
    </div>
  )
}

// ── Column headers ────────────────────────────────────────────────────────────
const TableHeader = ({ allChecked, onToggleAll }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: GRID_COLS,
      alignItems: "center",
      padding: "7px 16px",
      borderBottom: T.border,
      background: "#fdfcfb",
    }}
  >
    <Checkbox checked={allChecked} onClick={onToggleAll} />
    {["Candidate", "Match", "Experience", "Notice", "Applied", ""].map((h, i) => (
      <span
        key={i}
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.textTertiary,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          fontFamily: MONO,
          textAlign: i === 5 ? "right" : "left",
        }}
      >
        {h}
      </span>
    ))}
  </div>
)

// ── Shimmer rows ──────────────────────────────────────────────────────────────
const ShimmerRow = ({ isLast }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: GRID_COLS,
      alignItems: "center",
      padding: "13px 16px",
      borderBottom: isLast ? "none" : T.border,
    }}
  >
    <div style={{ width: 16, height: 16, borderRadius: 4, background: "#f3f4f6" }} />
    <div style={{ display: "flex", alignItems: "center", gap: 10, paddingRight: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: 7, background: "#f3f4f6" }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 13, width: "65%", background: "#f3f4f6", borderRadius: 4, marginBottom: 6 }} />
        <div style={{ height: 11, width: "45%", background: "#f9fafb", borderRadius: 4 }} />
      </div>
    </div>
    {[70, 40, 60, 60].map((w, i) => (
      <div key={i} style={{ height: 12, width: w, background: "#f3f4f6", borderRadius: 4 }} />
    ))}
    <div style={{ height: 28, width: 56, background: "#f3f4f6", borderRadius: T.radius, marginLeft: "auto" }} />
  </div>
)

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ searchQuery, onClear }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "64px 24px",
      border: T.border,
      borderRadius: T.radiusCard,
      background: T.bg,
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: T.radiusCard,
        border: T.border,
        background: "#f9fafb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
        boxShadow: T.shadow,
      }}
    >
      <Users size={18} color="#d1d5db" />
    </div>
    <p style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6, fontFamily: FF, letterSpacing: "-0.01em" }}>
      {searchQuery ? "No matches found" : "No applicants yet"}
    </p>
    <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary, marginBottom: 20, fontFamily: FF }}>
      {searchQuery ? "Try a different search term" : "Candidates who apply will appear here"}
    </p>
    {searchQuery && (
      <button
        onClick={onClear}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 32, padding: "0 14px", borderRadius: T.radius,
          border: T.border, background: T.bg, color: T.textMuted,
          fontSize: 12, fontWeight: 600, fontFamily: FF,
          cursor: "pointer", outline: "none", transition: "border-color 0.12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb" }}
      >
        Clear search
      </button>
    )}
  </div>
)

// ── Sort dropdown ─────────────────────────────────────────────────────────────
const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const current = SORT_OPTIONS.find((o) => o.value === value)

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          height: 34, padding: "0 12px", borderRadius: T.radius,
          border: T.border, background: T.bg,
          cursor: "pointer", outline: "none", transition: "border-color 0.12s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af" }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.borderColor = "#e5e7eb" }}
      >
        <span style={{ fontSize: 12, fontWeight: 500, color: T.textTertiary, fontFamily: FF }}>Sort:</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: FF }}>{current?.label}</span>
        <ChevronDown
          size={11} strokeWidth={2} color={T.textTertiary}
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
        />
      </button>

      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            background: T.bg, border: T.border, borderRadius: T.radiusCard,
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)", zIndex: 500,
            minWidth: 180, overflow: "hidden",
          }}
        >
          {SORT_OPTIONS.map((opt) => {
            const active = value === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false) }}
                style={{
                  display: "flex", width: "100%",
                  alignItems: "center", justifyContent: "space-between",
                  padding: "9px 14px", border: "none", borderBottom: T.border,
                  background: active ? T.bgMuted : "transparent",
                  cursor: "pointer", fontFamily: FF,
                  transition: "background 0.1s", outline: "none",
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = T.bgMuted }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent" }}
              >
                <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? T.text : T.textMuted, fontFamily: FF }}>
                  {opt.label}
                </span>
                {active && <Check size={12} strokeWidth={2.5} color={T.text} />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Candidate row ─────────────────────────────────────────────────────────────
const CandidateRow = ({ candidate, checked, onCheck, onView, index, total }) => {
  const score      = candidate.matchingScore?.finalScore ?? null
  const matchLevel = candidate.matchingScore?.matchLevel ?? null
  const appliedDate = candidate.appliedAt
    ? new Date(candidate.appliedAt).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })
    : null

  return (
    <div
      onClick={() => onCheck(candidate.applicantId)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_COLS,
        alignItems: "center",
        padding: "11px 16px",
        borderBottom: index !== total - 1 ? T.border : "none",
        cursor: "pointer",
        transition: "background 0.1s",
        background: checked ? "#f9fafb" : "transparent",
      }}
      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = T.bgMuted }}
      onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = "transparent" }}
    >
      {/* Checkbox */}
      <Checkbox checked={checked} onClick={() => onCheck(candidate.applicantId)} />

      {/* Candidate */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, paddingRight: 12 }}>
        <Avatar name={candidate.applicantName} />
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>
            {candidate.applicantName || "—"}
          </p>
          <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 500, color: T.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: FF }}>
            {candidate.personal_information?.currentJobTitle
              ? candidate.personal_information?.currentCompany
                ? `${candidate.personal_information.currentJobTitle} · ${candidate.personal_information.currentCompany}`
                : candidate.personal_information.currentJobTitle
              : candidate.personal_information?.currentCompany || "—"}
          </p>
        </div>
      </div>

      {/* Match */}
      <ScoreCell score={score} matchLevel={matchLevel} />

      {/* Experience */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Briefcase size={12} color="#d1d5db" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, fontFamily: FF }}>
          {candidate.personal_information?.yearsExperience ? `${candidate.personal_information.yearsExperience} yrs` : "—"}
        </span>
      </div>

      {/* Notice */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Clock size={12} color="#d1d5db" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, fontFamily: FF }}>
          {candidate.job_preferences?.noticePeriod || "—"}
        </span>
      </div>

      {/* Applied */}
      <div>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.textMuted, fontFamily: FF }}>
          {appliedDate || "—"}
        </span>
      </div>

      {/* View */}
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onView(candidate) }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            height: 28, padding: "0 10px",
            borderRadius: T.radius - 1, border: T.border,
            background: T.bgMuted, color: T.textMuted,
            fontSize: 12, fontWeight: 700, fontFamily: FF,
            letterSpacing: "0.03em", cursor: "pointer",
            outline: "none", transition: "opacity 0.12s", whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.75" }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = "1" }}
        >
          View <ChevronRight size={10} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

// ── Detail panel ──────────────────────────────────────────────────────────────
const DetailPanel = ({
  candidate,
  jobListing,
  rejectingCandidateId,
  onRejectCandidate,
  onScheduleInterview,
  onMessageCandidate,
  onStageChange,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}) => {
  useEffect(() => {
    document.body.style.overflow = "hidden"
    const handler = (e) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = ""
      document.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.28)",
          zIndex: 40,
          backdropFilter: "blur(1px)",
        }}
        onClick={onClose}
      />

      {/* ── Floating left controls ── */}
     <div
  style={{
    position: "fixed",
    left: "calc(21.5rem - 56px)",
    top: "60px",
    transform: "none",
    zIndex: 51,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  }}
>
  {/* Close */}
  <button
    onClick={onClose}
    style={{
      width: 42,
      height: 42,
      borderRadius: "50%",
      background: "#111827",
      border: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      transition: "background 0.15s",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "#374151" }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "#111827" }}
  >
    <X size={14} strokeWidth={2.5} />
  </button>

  {/* Prev + Next grouped */}
  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 24 }}>
    <button
      onClick={onPrev}
      disabled={!hasPrev}
      style={{
        width: 42,
        height: 42,
        borderRadius: "50%",
        background: hasPrev ? "#111827" : "#6b7280",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: hasPrev ? "pointer" : "not-allowed",
        color: "#fff",
        opacity: hasPrev ? 1 : 0.4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => { if (hasPrev) e.currentTarget.style.background = "#374151" }}
      onMouseLeave={(e) => { if (hasPrev) e.currentTarget.style.background = "#111827" }}
    >
      <span style={{ fontSize: 9, fontWeight: 700, fontFamily: MONO, letterSpacing: "0.05em" }}>
        PREV
      </span>
    </button>

    <button
      onClick={onNext}
      disabled={!hasNext}
      style={{
        width: 42,
        height: 42,
        borderRadius: "50%",
        background: hasNext ? "#111827" : "#6b7280",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: hasNext ? "pointer" : "not-allowed",
        color: "#fff",
        opacity: hasNext ? 1 : 0.4,
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => { if (hasNext) e.currentTarget.style.background = "#374151" }}
      onMouseLeave={(e) => { if (hasNext) e.currentTarget.style.background = "#111827" }}
    >
      <span style={{ fontSize: 9, fontWeight: 700, fontFamily: MONO, letterSpacing: "0.05em" }}>
        NEXT
      </span>
    </button>
  </div>
</div>

      <div
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100%",
          width: "calc(100vw - 21.5rem)",
          background: T.bg,
          borderLeft: T.border,
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 0 40px rgba(0,0,0,0.12)",
          fontFamily: FF,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "13px 20px",
            borderBottom: T.border,
            background: T.bgMuted,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#111827",
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: T.textTertiary,
                fontFamily: MONO,
              }}
            >
              Application
            </span>
            {candidate?.applicantName && (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.text,
                  background: T.bg,
                  border: T.border,
                  borderRadius: 5,
                  padding: "2px 9px",
                  fontFamily: FF,
                }}
              >
                {candidate.applicantName}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: T.bg,
              border: T.border,
              borderRadius: T.radius - 1,
              cursor: "pointer",
              color: T.textTertiary,
              transition: "all 0.12s",
              outline: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#111827"
              e.currentTarget.style.color = "#fff"
              e.currentTarget.style.borderColor = "#111827"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = T.bg
              e.currentTarget.style.color = T.textTertiary
              e.currentTarget.style.borderColor = "#e5e7eb"
            }}
          >
            <X size={13} strokeWidth={2} />
          </button>
        </div>
        {/* Body */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <CandidateDetailsContent
            orgId={jobListing?.organizationId}
            jobId={jobListing?.id}
            selectedCandidate={candidate}
            jobListing={jobListing}
            rejectingCandidateId={rejectingCandidateId}
            onRejectCandidate={onRejectCandidate}
            onScheduleInterview={onScheduleInterview}
            onMessageCandidate={onMessageCandidate}
            onStageChange={onStageChange}
          />
        </div>
      </div>
    </>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
const CandidateListContent = ({
  jobListing,
  rejectingCandidateId,
  onRejectCandidate,
  onScheduleInterview,
  evaluationProgress,
  onStageChange,
  isLoading = false,
}) => {
  const candidates = jobListing?.applicants || []

  const [searchQuery, setSearchQuery] = useState("")
  const [sortBy, setSortBy] = useState("appliedDesc")
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [emailTarget, setEmailTarget] = useState(null)
  const [emailManyRecipients, setEmailManyRecipients] = useState(null)
  const [selectedCandidate, setSelectedCandidate] = useState(null)

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setSelectedCandidate(null)
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [])

  const handleMessageCandidate = useCallback(
    (candidate) => {
      const context = createCandidateContext({ candidate, jobListing })
      setEmailTarget({ candidate, context })
    },
    [jobListing],
  )

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = (ids, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)))
      return next
    })
  }

  // ── Archive confirm — mirrors Leads.jsx handleArchiveConfirm exactly ──────
  const handleArchiveConfirm = useCallback(
    ({ selectedIds: archiveIds, reason, sendEmail }) => {
      setShowArchiveModal(false)

      if (sendEmail) {
        const archiveCandidates = candidates.filter((c) =>
          archiveIds.has(c.applicantId),
        )

        if (archiveCandidates.length === 1) {
          const candidate = archiveCandidates[0]
          setEmailTarget({
            candidate,
            context: createCandidateContext({ candidate, jobListing }),
          })
        } else if (archiveCandidates.length > 1) {
          setEmailManyRecipients(
            archiveCandidates.map((candidate) => ({
              applicantId: candidate.applicantId,
              name: candidate.applicantName,
              email: candidate.applicantEmail,
              context: createCandidateContext({ candidate, jobListing }),
            })),
          )
        }
      }

      // TODO: call archive API with archiveIds + reason
      setSelectedIds(new Set())
    },
    [candidates, jobListing],
  )

  // ── Processed list ────────────────────────────────────────────────────────
  const processedCandidates = (() => {
    let list = candidates.filter((c) => c.applicationStatus === "applied")
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (c) =>
          c.applicantName?.toLowerCase().includes(q) ||
          c.personal_information?.currentJobTitle?.toLowerCase().includes(q) ||
          c.personal_information?.currentCompany?.toLowerCase().includes(q),
      )
    }
    if (SORT_COMPARATORS[sortBy]) list.sort(SORT_COMPARATORS[sortBy])
    return list
  })()

  // ── Processed list (add this) ──
  const selectedIndex = selectedCandidate
    ? processedCandidates.findIndex(
        (c) => c.applicantId === selectedCandidate.applicantId,
      )
    : -1

  const handlePrev = useCallback(() => {
    if (selectedIndex > 0)
      setSelectedCandidate(processedCandidates[selectedIndex - 1])
  }, [selectedIndex, processedCandidates])

  const handleNext = useCallback(() => {
    if (selectedIndex < processedCandidates.length - 1)
      setSelectedCandidate(processedCandidates[selectedIndex + 1])
  }, [selectedIndex, processedCandidates])

  const clearSearch = () => setSearchQuery("")
  const hasSelection = selectedIds.size > 0
  const visibleIds = processedCandidates.map((c) => c.applicantId)
  const allChecked =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))

  return (
    <div
      style={{
        fontFamily: FF,
        background: T.bgPage,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes clc-spin { to { transform: rotate(360deg); } }
        .clc-scroll::-webkit-scrollbar { display: none; }
        .clc-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ── Top bar ── */}
      <div
        style={{
          background: T.bg,
          borderBottom: T.border,
          padding: "13px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        {/* Left */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#111827",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: T.textTertiary,
              fontFamily: MONO,
            }}
          >
            Applicants
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: T.textTertiary,
              background: "#f3f4f6",
              border: T.border,
              borderRadius: 5,
              padding: "1px 7px",
              fontFamily: MONO,
            }}
          >
            {processedCandidates.length}
          </span>

          {evaluationProgress?.status === "processing" && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 8px",
                borderRadius: T.radius,
                background: "#eff6ff",
                color: "#1d4ed8",
                border: "0.5px solid #bfdbfe",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: MONO,
              }}
            >
              <RefreshCw
                size={10}
                style={{
                  animation: "clc-spin 0.9s linear infinite",
                  flexShrink: 0,
                }}
              />
              Evaluating {evaluationProgress.progress.current}/
              {evaluationProgress.progress.total}
            </span>
          )}
        </div>

        {/* Right */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Archive — visible only when rows selected, identical style to Leads.jsx */}
          {hasSelection && (
            <button
              onClick={() => setShowArchiveModal(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 34,
                padding: "0 12px",
                borderRadius: T.radius,
                border: T.border,
                background: T.bg,
                color: T.textMuted,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FF,
                cursor: "pointer",
                outline: "none",
                transition: "border-color 0.12s, color 0.12s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#9ca3af"
                e.currentTarget.style.color = T.text
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb"
                e.currentTarget.style.color = T.textMuted
              }}
            >
              <Archive size={12} strokeWidth={2} />
              Archive{selectedIds.size > 1 ? ` (${selectedIds.size})` : ""}
            </button>
          )}

          {/* Search */}
          <div style={{ position: "relative", height: 34 }}>
            <div
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Search size={13} color={T.textTertiary} />
            </div>
            <input
              type="text"
              placeholder="Search applicants…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                height: "100%",
                paddingLeft: 30,
                paddingRight: searchQuery ? 28 : 12,
                width: 210,
                border: T.border,
                borderRadius: T.radius,
                fontSize: 13,
                fontWeight: 500,
                color: T.text,
                fontFamily: FF,
                background: T.bg,
                outline: "none",
                transition: "border-color 0.12s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#9ca3af"
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb"
              }}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.textTertiary,
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                  outline: "none",
                }}
              >
                <X size={11} />
              </button>
            )}
          </div>

          {/* Sort */}
          <SortDropdown value={sortBy} onChange={setSortBy} />
        </div>
      </div>

      {/* ── Scrollable list ── */}
      <div
        className="clc-scroll"
        style={{ flex: 1, overflowY: "auto", padding: "20px 20px 48px" }}
      >
        {isLoading ? (
          <div
            style={{
              background: T.bg,
              borderRadius: T.radiusCard,
              border: T.border,
              boxShadow: T.shadow,
              overflow: "hidden",
            }}
          >
            <TableHeader allChecked={false} onToggleAll={() => {}} />
            {Array.from({ length: 8 }).map((_, i) => (
              <ShimmerRow key={i} isLast={i === 7} />
            ))}
          </div>
        ) : processedCandidates.length === 0 ? (
          <EmptyState searchQuery={searchQuery} onClear={clearSearch} />
        ) : (
          <>
            <div
              style={{
                background: T.bg,
                borderRadius: T.radiusCard,
                border: T.border,
                boxShadow: T.shadow,
                overflow: "hidden",
              }}
            >
              <TableHeader
                allChecked={allChecked}
                onToggleAll={() => toggleAll(visibleIds, !allChecked)}
              />
              {processedCandidates.map((candidate, index) => (
                <CandidateRow
                  key={candidate.applicantId}
                  candidate={candidate}
                  checked={selectedIds.has(candidate.applicantId)}
                  onCheck={toggleOne}
                  onView={setSelectedCandidate}
                  index={index}
                  total={processedCandidates.length}
                />
              ))}
            </div>

            {/* Footer count + selection indicator */}
            <div
              style={{
                marginTop: 12,
                paddingLeft: 4,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: T.textTertiary,
                  fontFamily: FF,
                }}
              >
                {processedCandidates.length} of {candidates.length}{" "}
                {candidates.length === 1 ? "applicant" : "applicants"}
              </span>
              {hasSelection && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.text,
                    fontFamily: FF,
                  }}
                >
                  · {selectedIds.size} selected
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Detail panel ── */}
      {selectedCandidate && (
        <DetailPanel
          candidate={selectedCandidate}
          jobListing={jobListing}
          rejectingCandidateId={rejectingCandidateId}
          onRejectCandidate={async (c) => {
            await onRejectCandidate(c)
          }}
          onScheduleInterview={onScheduleInterview}
          onMessageCandidate={handleMessageCandidate}
          onStageChange={onStageChange}
          onClose={() => setSelectedCandidate(null)}
          onPrev={handlePrev}
          onNext={handleNext}
          hasPrev={selectedIndex > 0}
          hasNext={selectedIndex < processedCandidates.length - 1}
        />
      )}

      {/* ── Archive modal ── */}
      {showArchiveModal && (
        <ArchiveLeadModal
          selectedIds={selectedIds}
          jobListing={jobListing}
          onClose={() => setShowArchiveModal(false)}
          onArchive={handleArchiveConfirm}
        />
      )}

      {/* ── Email many (bulk archive follow-up) ── */}
      {emailManyRecipients && (
        <EmailModalMany
          isOpen
          recipients={emailManyRecipients}
          organizationId={jobListing?.organizationId ?? ""}
          organizationName={jobListing?.organizationName ?? ""}
          senderId={jobListing?.organizationId ?? ""}
          logoUrl={jobListing?.logoUrl ?? ""}
          onClose={() => setEmailManyRecipients(null)}
          onSendComplete={() => setEmailManyRecipients(null)}
        />
      )}

      {/* ── Email single ── */}
      {emailTarget && (
        <EmailModal
          isOpen
          onClose={() => setEmailTarget(null)}
          candidateName={emailTarget.candidate.applicantName}
          candidateEmail={emailTarget.candidate.applicantEmail}
          applicantId={emailTarget.candidate.applicantId}
          organizationId={jobListing?.organizationId ?? ""}
          organizationName={jobListing?.organizationName ?? ""}
          senderId={jobListing?.organizationId ?? ""}
          logoUrl={jobListing?.logoUrl ?? ""}
          context={emailTarget.context}
        />
      )}
    </div>
  )
}

export default CandidateListContent